import { jobApi } from '@/helpers';
import { useAlertsStore } from '@/stores/alerts';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import { useWorkpackagesStore } from '@/stores/workpackages';
import spacebreakUtils from '@/utils/spacebreak-utils';
import to from 'await-to-js';
import axios from 'axios';
import {
  assign,
  cloneDeep,
  difference,
  filter,
  find,
  flatMap,
  flatten,
  forEach,
  get,
  groupBy,
  intersection,
  isEmpty,
  isNil,
  keyBy,
  map,
  merge,
  orderBy,
  reduce,
  reject,
  set,
  size,
  some,
  sortBy,
  transform,
  uniq,
  uniqueId,
  values,
} from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import i18n from '@/plugins/vue-i18n';
import { useEditableTableStore } from '@/stores/utils/editable-table-store-utils';
import MappingUtils from './utils/furniture-to-store-mapping';
import handleErrorMessages from './utils/validation';

// So that is is accessible by mutations
function getMostCommonFurniture(allGeneratedFurniture) {
  const order = ['desc', 'desc', 'asc'];
  const fields = ['storeCount', 'size', 'furnitureId'];
  return orderBy(allGeneratedFurniture, fields, order)[0] || null;
}

const NEWLY_ADDED_ID_PREFIX = 'newlyAddedIdPrefix';
export const useFurnitureStore = defineStore('furniture', () => {
  const contextStore = useContextStore();
  const scenariosStore = useScenariosStore();
  const clusteringStore = useClusteringStore();
  const workpackagesStore = useWorkpackagesStore();
  const snackbarStore = useSnackbarStore();
  const alertsStore = useAlertsStore();

  const {
    // States
    localDataGetterName,
    localDataChangesPerCell,
    localTableRowContents,
    localInvalidErrors,
    // Getters
    getTableRows,
    hasInvalidValuesInAnyCell,
    userMadeChangesToAnyCell,
    hasDataIn,
    hasChangesInCell,
    hasInvalidDataInCell,
    // Actions
    setNameOfLocalGetter,
    setTableRows,
    setInputValue,
    setInputError,
    setCellChanges,
    unloadData,
    markAllRowsAsChanged,
    initialiseEditableTable,
    onValueChanged,
    setUploadedData,
  } = useEditableTableStore();

  // state
  const scenarioFurniture = ref({});
  const workpackageFurniture = ref({});
  const workpackageScenarioFurniture = ref([]);
  const workpackageProductFurniture = ref([]);
  const isLoading = shallowRef(false);
  const isLoadingAllFurniture = shallowRef(true);
  const availableFurnitureIds = ref({});
  const newlyAddedIdPrefix = shallowRef(NEWLY_ADDED_ID_PREFIX);

  // getters
  const getWorkpackageFurniture = computed(() => workpackageFurniture.value);

  const getFurnituresInScenario = computed(() => {
    return sortBy(scenarioFurniture.value.furnitureType, 'size');
  });

  const getGeneratedFurnituresInScenario = computed(() => {
    return sortBy(scenarioFurniture.value.generatedFurniture, 'size');
  });

  const getStoreClassesInScenario = computed(() => {
    return sortBy(scenarioFurniture.value.storeClasses, 'name');
  });

  const getStoreMapping = computed(() => {
    return scenarioFurniture.value.storeFurniture;
  });

  const getAllAvailableFurnitureIds = computed(() => {
    const furnitureIdsInSpacebreaks = flatMap(
      flatMap(scenarioFurniture.value.storeClasses, 'spacebreaks'),
      'generatedFurnitureIds'
    );
    const allFurnitureIds = flatMap(scenarioFurniture.value.storeClasses, 'generatedFurnitureIds');
    return difference(allFurnitureIds, furnitureIdsInSpacebreaks);
  });

  const countFurnitures = computed(
    () => furnitures =>
      scenarioFurniture.value.generatedFurniture.reduce(
        (count, generatedFurniture) =>
          furnitures.includes(generatedFurniture._id)
            ? count + size(generatedFurniture.storeKeys)
            : count,
        0
      )
  );

  const getFurnitureToStoreMapping = computed(() => {
    const storeFurniture = groupBy(scenarioFurniture.value.storeFurniture, 'furnitureId');
    return reduce(
      getFurnituresInScenario,
      (acc, sf) => {
        const storeKeys = flatMap(storeFurniture[sf._id], 'storeKey');
        acc.push({ ...sf, storeKeys });
        return acc;
      },
      []
    );
  });

  const getWorkpackageProductFurniture = computed(() => {
    return reduce(
      workpackageProductFurniture.value.productFurniture || [],
      (acc, { furnitureId, productKeys }) => {
        acc[furnitureId] = productKeys.length;
        return acc;
      },
      {}
    );
  });

  const getSimpleSwapsClusteredFurnitureMapping = computed(() => {
    const scenarioClusteredStoreMap = scenariosStore.getSimpleSwapsClusteredStores;
    const storeFurniture = groupBy(scenarioFurniture.value.storeFurniture, 'furnitureId') || [];
    const productFurniture = getWorkpackageProductFurniture.value;
    const furnitureSelection = scenarioFurniture.value.simpleSwapsFurnitureSelection || [];

    // StoreClass furniture mapping
    const storeClassLookup = getStoreClassesInScenario.value.reduce((mappings, storeClass) => {
      const storeClassFurnitureMappings = storeClass.furnitureIds.reduce((acc, id) => {
        acc[id] = storeClass.name;
        return acc;
      }, {});
      return assign(mappings, storeClassFurnitureMappings);
    }, {});

    // Furnitures in workpackage with store keys
    const localScenarioFurniture = scenarioFurniture.value.furnitureType.reduce((acc, sf) => {
      const storeKeys = flatMap(storeFurniture[sf._id], 'storeKey');
      acc.push({ ...sf, storeKeys });
      return acc;
    }, []);

    // Returns furniture data keyed by cluster
    return reduce(
      scenarioClusteredStoreMap,
      (acc, storeKeys, index) => {
        forEach(localScenarioFurniture, f => {
          const matchingKeys = intersection(f.storeKeys, storeKeys);
          const numberOfStores = size(matchingKeys);

          if (numberOfStores) {
            const storeClass = storeClassLookup[f._id];
            const numberOfProducts = productFurniture[f._id];
            const furnitureSelectionMap = furnitureSelection.find(
              fs => fs.furnitureId === f._id && fs.cluster === index
            );
            const inScope = size(furnitureSelectionMap) > 0 ? furnitureSelectionMap.inScope : null;

            acc.push({
              cluster: index,
              storeClass,
              furnitureId: f._id,
              furnitureName: f.name,
              storeKeys: matchingKeys,
              numberOfProducts,
              numberOfStores,
              inScope,
            });
          }
        });
        return acc;
      },
      []
    );
  });

  const getFurnitureToStoreMappingSorted = computed(() => {
    const furnitureMap = keyBy(getFurnituresInScenario.value, '_id');
    const wpStores = keyBy(workpackagesStore.selectedWorkpackage.stores, 'storeKey');
    const storeMap = transform(
      scenariosStore.stores,
      (acc, scStore) => {
        if (wpStores[scStore.storeKey].assortment) {
          acc[scStore.storeKey] = scStore;
        }
      },
      {}
    );

    return MappingUtils.buildTableContents(
      furnitureMap,
      storeMap,
      scenarioFurniture.value.storeFurniture
    );
  });

  const spacebreakNameIsUnique = computed(() => ({ spacebreak, storeClassId }) => {
    const otherSpacebreaks = scenarioFurniture.value.storeClasses
      .find(({ _id }) => _id === storeClassId)
      .spacebreaks.filter(sb => sb._id !== spacebreak._id);
    if (otherSpacebreaks.filter(sb => sb.name.trim() === spacebreak.name.trim()).length) {
      return i18n.t('validationErrors.unique', [i18n.t('spacebreakCalculatorPage.spacebreak')]);
    }
    return '';
  });

  const getSpacebreaksIndexedById = computed(() => {
    // index space breaks by id.
    const selectedClusterScheme = cloneDeep(clusteringStore.selectedClusterScheme);
    const clusters = get(selectedClusterScheme, 'clusters', []);
    // need this for the unclustered points of distribution
    clusters.push({ clusterId: null, storeKeys: [] });
    // index space breaks by id.
    const fillInSelection = workpackagesStore.selectedWorkpackage.fillInSelection;
    const spacebreaks = reduce(
      scenarioFurniture.value.storeClasses,
      (acc, sc) => {
        const productPositioningInfoPerSpacebreak = reduce(
          sc.spacebreaks,
          (sbAcc, sb) => {
            sb.shortName = spacebreakUtils.getSpacebreakShortName({
              spacebreak: sb,
              fillInSelection,
            });
            set(sbAcc, sb._id, sb);
            return sbAcc;
          },
          {}
        );
        return { ...acc, ...productPositioningInfoPerSpacebreak };
      },
      {}
    );

    const clusteredSpacebreaks = {};
    clusters.forEach(c => {
      clusteredSpacebreaks[c.clusterId] = {};
      Object.entries(spacebreaks).forEach(([sbKey, sb]) => {
        const cpSb = cloneDeep(sb);
        // only include stores that are available in cluster
        cpSb.storeKeys =
          get(c, 'storeKeys', []).length > 0
            ? intersection(cpSb.storeKeys, c.storeKeys)
            : cpSb.storeKeys;
        clusteredSpacebreaks[c.clusterId][sbKey] = cpSb;
      });
    });
    return clusteredSpacebreaks;
  });

  const spacebreaksShortNamesByCluster = computed(() => {
    return reduce(
      getSpacebreaksIndexedById.value,
      (acc, indexedSpacebreak, clusterId) => {
        acc[clusterId] = values(indexedSpacebreak).map(s => ({
          value: s._id,
          text: s.shortName,
        }));
        return acc;
      },
      {}
    );
  });

  const wpScenarioFurnitureMap = computed(() => {
    return reduce(
      workpackageScenarioFurniture.value,
      (acc, furniture) => {
        return {
          ...acc,
          [furniture.scenarioId]: furniture.storeClasses,
        };
      },
      {}
    );
  });

  const hasFurnitureType = computed(() => furnitureType => {
    return some(scenarioFurniture.value.furnitureType, f => f.type === furnitureType);
  });
  const groupedFurnitureByName = computed(() => groupBy(getFurnituresInScenario.value, 'name'));

  const getFurnitureArchetypeName = computed(() => ({ data, groupedFurnitureByNameLocal }) => {
    const hasSpacebreakFromDataEnabled = get(
      contextStore.clientConfig,
      'features.spacebreaksFromDataEnabled',
      false
    );

    const name = get(data, 'name', ' ');
    const furnitureByName = groupedFurnitureByNameLocal || groupedFurnitureByName.value;

    // if generate spacebreak from data is not enabled or
    // if there is no duplication
    // return unmodified archetype name
    if (!hasSpacebreakFromDataEnabled || name === ' ' || size(furnitureByName[name]) === 1) {
      return name;
    }

    // otherwise add store class suffix
    const furnitureId = data._id;
    const storeClasses = getStoreClassesInScenario.value;

    const suffix = get(
      storeClasses.find(sc => sc.furnitureIds.includes(furnitureId)),
      'name',
      ''
    );

    return `${name} (${suffix})`;
  });

  // Helpers
  function getSpacebreak({ storeClassId, spacebreakId }) {
    const storeClass = find(scenarioFurniture.value.storeClasses, { _id: storeClassId });
    return find(storeClass.spacebreaks, { _id: spacebreakId });
  }
  function getGeneratedFurniture(furnitureId) {
    return find(scenarioFurniture.value.generatedFurniture, { _id: furnitureId });
  }
  function getFurnituresInSpacebreak({ storeClassId, spacebreakId }) {
    const spacebreak = getSpacebreak({ storeClassId, spacebreakId });

    return spacebreak.generatedFurnitureIds.map(id => getGeneratedFurniture(id));
  }

  // Actions
  async function fetchWorkpackageFurniture({ workpackageId } = {}) {
    isLoadingAllFurniture.value = true;

    let id = workpackageId;
    if (!workpackageId) {
      id = workpackagesStore.selectedWorkpackage._id;
    }

    const [err, res] = await to(axios.get(`/api/workpackages/${id}/furniture`));
    const furnitureData = get(res, 'data');

    isLoadingAllFurniture.value = false;
    if (err) throw new Error(err.message);
    // if no furnitureData or an empty string returned - set to empty object
    workpackageFurniture.value = furnitureData || {};
  }

  async function updateWorkpackageFurniture({ workpackageId, updates }) {
    isLoadingAllFurniture.value = true;

    const [err, result] = await to(
      axios.patch(`/api/workpackages/${workpackageId}/furniture`, updates)
    );
    isLoadingAllFurniture.value = false;
    if (err) {
      const message = get(err, ['response', 'data', 'message'], err.message);
      throw new Error(message);
    }

    if (result) {
      fetchWorkpackageFurniture({ workpackageId });
    }
  }

  async function fetchScenarioFurniture({ options = {} } = {}) {
    isLoadingAllFurniture.value = true;
    const { _id: scenarioId } = scenariosStore.selectedScenario;

    const [err, res] = await to(
      axios.get(`/api/scenarios/${scenarioId}/furniture`, { params: options.params })
    );
    const furnitureData = get(res, 'data');

    isLoadingAllFurniture.value = false;
    if (err) throw new Error(err.message);
    scenarioFurniture.value = furnitureData;
  }

  async function fetchScenarioFurnitureByWorkPackage(options = {}) {
    const workpackageId = options.workpackageId || workpackagesStore.selectedWorkpackage._id;
    const [err, res] = await to(
      axios.get(`/api/workpackages/${workpackageId}/scenarios/furniture`)
    );
    if (err) throw new Error(err.message);
    const furnitureData = get(res, 'data');
    if (!options.shouldCommit) return furnitureData;
    workpackageScenarioFurniture.value = furnitureData;
  }

  async function fetchFurnitureProductsByWorkpackage() {
    const workpackageId = workpackagesStore.selectedWorkpackage._id;
    const [err, res] = await to(axios.get(`/api/workpackages/${workpackageId}/furniture-products`));
    if (err) throw new Error(err.message);
    const furnitureProductData = get(res, 'data');
    workpackageProductFurniture.value = furnitureProductData;
  }

  async function saveScenarioFurniture({ dispatchOptions, ...updates }) {
    isLoading.value = true;
    const { _id: scenarioId } = scenariosStore.selectedScenario;
    let deletedStoreClasses = [];
    let addedStoreClasses = [];

    if (updates.storeClasses) {
      deletedStoreClasses = difference(
        map(scenarioFurniture.value.storeClasses, '_id'),
        map(updates.storeClasses, '_id')
      );
      // new store class doesn't have id, that is why we should use name
      addedStoreClasses = map(
        filter(updates.storeClasses, sc => isNil(sc._id)),
        'name'
      );

      updates.storeClasses = updates.storeClasses.map(sc => {
        sc.spacebreaks = sc.spacebreaks.map(sb => {
          if (sb._id.length < 20) {
            sb._id = null;
          }
          return sb;
        });
        return sc;
      });
    }

    const [err, response] = await to(
      axios.patch(`/api/scenarios/${scenarioId}/furniture/${scenarioFurniture.value._id}`, {
        deletedStoreClasses,
        addedStoreClasses,
        updates,
      })
    );
    if (err) {
      isLoading.value = false;
      throw new Error(err.message);
    }

    scenarioFurniture.value = response.data;

    if (get(dispatchOptions, 'showMessage', true)) {
      snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
    }

    isLoading.value = false;
    return response;
  }

  async function runWorkpackageFurnitureSetup() {
    const config = contextStore.clientConfig;
    const excludeNonMappedFurniture = get(config, 'features.excludeFurnitureNotMapped', false);
    const furnitureToBePulledForAssortmentAGsOnly = get(
      config,
      'features.furnitureToBePulledForAssortmentAGsOnly',
      false
    );
    const removeUnsizedFurniture = get(config, 'features.removeUnsizedFurniture', false);

    const [err] = await to(
      jobApi.runFunction('run-furniture-setup', {
        workpackage_id: workpackagesStore.selectedWorkpackage._id,
        // These params must be in sync with `analytics-job` getRequestParams for WP setup
        exclude_non_mapped_furniture: excludeNonMappedFurniture,
        furniture_for_assortment_ags_only: furnitureToBePulledForAssortmentAGsOnly,
        remove_unsized_furniture: removeUnsizedFurniture,
        generate_spacebreaks: false, // sbs must only be generated on WP setup
      })
    );
    if (err) throw new Error(err.message);
  }

  // furnitureService is one of: furnitureType, furniture to store, storeClasses, etc
  async function processCSV({ fileId, mappings, furnitureService, delimiter }) {
    const scenarioId = scenariosStore.selectedScenario._id;
    isLoading.value = true;
    const body = { mappings, scenarioId, delimiter };
    const [err, { data: responseData } = {}] = await to(
      axios.post(`/api/csv/${furnitureService}/process/${fileId}`, body)
    );
    isLoading.value = false;
    // TODO: Remove this and use global interceptor on AOV3-159
    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));
      snackbarStore.showError(message);
      return Promise.reject(message);
    }
    if (size(responseData.messages)) {
      alertsStore.showMessages(responseData.messages);
    }
    const csvRows = responseData.tableRows;
    csvRows.forEach(row => {
      // add temp ID to uploaded entries
      row._id = row._id || uniqueId(`${NEWLY_ADDED_ID_PREFIX}-${furnitureService}`);
    });

    return responseData.tableRows;
  }

  async function startSpacebreaksJob() {
    const { _id: scenarioFurnitureId } = scenarioFurniture.value;
    const { _id: scenarioId } = scenariosStore.selectedScenario;
    isLoading.value = true;
    const [err, response] = await to(
      axios.post(
        `/api/scenarios/${scenarioId}/furniture/${scenarioFurnitureId}/spacebreak-generator`,
        {}
      )
    );
    isLoading.value = false;
    if (err) throw new Error(err);
    scenariosStore.loadScenario(scenarioId);
    handleErrorMessages({ response });
  }

  async function spacebreaksJobCompleted({ message }) {
    const selectedScenario = scenariosStore.selectedScenario;
    if (message.scenarioId === selectedScenario._id) {
      fetchScenarioFurniture();
    }
  }

  function setScenarioFurniture(val) {
    scenarioFurniture.value = val;
  }

  function removeOneFurnitureFromSpacebreak({ storeClassId, spacebreakId, furnitureId }) {
    const spacebreak = getSpacebreak({ storeClassId, spacebreakId });

    // remove stores from removed furniture, but only if they are not in the other furnitures
    const otherFurnitures = map(
      reject(getFurnituresInSpacebreak({ storeClassId, spacebreakId }), { _id: furnitureId }),
      of => ({ ...of, storeCount: size(of.storeKeys) })
    );
    const storesInOtherFurnitures = flatten(map(otherFurnitures, 'storeKeys'));
    spacebreak.generatedFurnitureIds = spacebreak.generatedFurnitureIds.filter(
      fid => fid !== furnitureId
    );
    spacebreak.storeKeys = spacebreak.storeKeys.filter(sk => storesInOtherFurnitures.includes(sk));
    const mcf = getMostCommonFurniture(otherFurnitures);
    spacebreak.size = get(mcf, 'size', 0);
    if (isEmpty(spacebreak.generatedFurnitureIds) && !get(mcf, 'size', 0)) {
      spacebreak.fillOverride = 0;
    }
  }

  function removeAllFurnitureFromSpacebreak({ storeClassId, spacebreakId }) {
    const spacebreak = getSpacebreak({ storeClassId, spacebreakId });
    spacebreak.generatedFurnitureIds = [];
    spacebreak.storeKeys = [];
    spacebreak.size = 0;
    spacebreak.fillOverride = 0;
  }

  function addSpacebreak({ storeClassId }) {
    const seed = Date.now();
    const storeClass = scenarioFurniture.value.storeClasses.find(({ _id }) => _id === storeClassId);

    const newSpacebreak = {
      _id: uniqueId(seed),
      storeKeys: [],
      generatedFurnitureIds: [],
      size: 0,
      fillSuggested: 0,
      fillOverride: 0,
      name: '',
      shortName: null,
    };
    storeClass.spacebreaks.unshift(newSpacebreak);
  }

  function removeSpacebreakFromStoreClass({ storeClassId, spacebreakId }) {
    const storeClass = scenarioFurniture.value.storeClasses.find(({ _id }) => _id === storeClassId);
    storeClass.spacebreaks = storeClass.spacebreaks.filter(sb => sb._id !== spacebreakId);
  }

  function addAllAvailableFurnitureToSpacebreak({ storeClassId, spacebreakId, furnitureIdsToAdd }) {
    if (isEmpty(furnitureIdsToAdd)) return false;

    furnitureIdsToAdd.forEach(furnitureId =>
      this.addOneFurnitureToSpacebreak({ storeClassId, spacebreakId, furnitureId })
    );
  }

  function addOneFurnitureToSpacebreak({ storeClassId, spacebreakId, furnitureId }) {
    const spacebreak = getSpacebreak({ storeClassId, spacebreakId });
    const f = scenarioFurniture.value.generatedFurniture.find(gf => gf._id === furnitureId);
    spacebreak.generatedFurnitureIds = [...spacebreak.generatedFurnitureIds, furnitureId];
    spacebreak.storeKeys = uniq([...spacebreak.storeKeys, ...f.storeKeys]).sort();
    // Add storeCount to furniture - it's required to sort furnitures in getMostCommonFurniture
    const allGF = map(
      filter(scenarioFurniture.value.generatedFurniture, gf =>
        spacebreak.generatedFurnitureIds.includes(gf._id)
      ),
      agf => ({ ...agf, storeCount: size(agf.storeKeys) })
    );
    const mcf = getMostCommonFurniture(allGF);
    spacebreak.size = get(mcf, 'size', 0);
    if (isEmpty(spacebreak.generatedFurnitureIds) && !get(mcf, 'size', 0)) {
      spacebreak.fillOverride = 0;
    }
  }

  function updateSpacebreakForStoreClass({ storeClassId, updatedSpacebreak }) {
    const spacebreak = getSpacebreak({
      storeClassId,
      spacebreakId: updatedSpacebreak._id,
    });
    merge(spacebreak, updatedSpacebreak);
  }

  return {
    scenarioFurniture,
    workpackageFurniture,
    workpackageScenarioFurniture,
    isLoading,
    isLoadingAllFurniture,
    availableFurnitureIds,
    newlyAddedIdPrefix,
    getWorkpackageFurniture,
    getFurnituresInScenario,
    getGeneratedFurnituresInScenario,
    getStoreClassesInScenario,
    getStoreMapping,
    getAllAvailableFurnitureIds,
    countFurnitures,
    getFurnitureToStoreMapping,
    getWorkpackageProductFurniture,
    getSimpleSwapsClusteredFurnitureMapping,
    getFurnitureToStoreMappingSorted,
    spacebreakNameIsUnique,
    getSpacebreaksIndexedById,
    spacebreaksShortNamesByCluster,
    wpScenarioFurnitureMap,
    hasFurnitureType,
    groupedFurnitureByName,
    getFurnitureArchetypeName,
    // Actions
    updateWorkpackageFurniture,
    fetchScenarioFurniture,
    fetchScenarioFurnitureByWorkPackage,
    fetchFurnitureProductsByWorkpackage,
    saveScenarioFurniture,
    removeOneFurnitureFromSpacebreak,
    removeAllFurnitureFromSpacebreak,
    runWorkpackageFurnitureSetup,
    processCSV,
    startSpacebreaksJob,
    spacebreaksJobCompleted,
    fetchWorkpackageFurniture,
    setScenarioFurniture,
    removeSpacebreakFromStoreClass,
    addOneFurnitureToSpacebreak,
    addAllAvailableFurnitureToSpacebreak,
    updateSpacebreakForStoreClass,
    // Editable table
    // States
    localDataGetterName,
    localDataChangesPerCell,
    localTableRowContents,
    localInvalidErrors,
    // Getters
    getTableRows,
    hasInvalidValuesInAnyCell,
    userMadeChangesToAnyCell,
    hasDataIn,
    hasChangesInCell,
    hasInvalidDataInCell,
    // Actions
    setNameOfLocalGetter,
    setTableRows,
    setInputValue,
    setInputError,
    setCellChanges,
    unloadData,
    markAllRowsAsChanged,
    initialiseEditableTable,
    onValueChanged,
    setUploadedData,
    addSpacebreak,
  };
});
